<template>
  <div
    class="prepeers-checkbox"
    :class="{ 'prepeers-checkbox--disabled': props.disabled }">
    <label class="prepeers-checkbox__label">
      <input
        :id="props.id"
        class="prepeers-checkbox__input"
        type="checkbox"
        :checked="props.modelValue"
        :disabled="props.disabled"
        :aria-invalid="props.error ? 'true' : undefined"
        :aria-describedby="props.error ? errorElementId : undefined"
        @change="onChange" />
      <span
        class="prepeers-checkbox__box"
        :class="{ 'prepeers-checkbox__box--error': !!props.error }"
        aria-hidden="true">
        <span class="prepeers-checkbox__check">
          <PrePeersIcon name="check" />
        </span>
      </span>
      <span class="prepeers-checkbox__text">
        <slot />
      </span>
    </label>
    <p
      v-if="props.error"
      :id="errorElementId"
      class="prepeers-checkbox__error"
      role="alert">
      {{ props.error }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import type { ComputedRef, PropType } from 'vue'
import type { PrePeersCheckboxEmit, PrePeersCheckboxProps } from '~/types/PrePeersCheckbox'
import { computed } from 'vue'

import PrePeersIcon from '~/components/icons/PrePeersIcon.vue'

const props: PrePeersCheckboxProps = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Boolean,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
})

const emit: PrePeersCheckboxEmit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void
}>()

const errorElementId: ComputedRef<string> = computed((): string => `${props.id}-error`)

/**
 * Transmet au parent le nouvel état de la case.
 * @param {Event} event - Changement de la case.
 * @returns {void}
 */
function onChange(event: Event): void {
  if (event.target instanceof HTMLInputElement) emit('update:modelValue', event.target.checked)
}
</script>

<style scoped>
.prepeers-checkbox {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.prepeers-checkbox__label {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text-secondary);
  text-align: left;
  cursor: pointer;
}

.prepeers-checkbox--disabled .prepeers-checkbox__label {
  cursor: not-allowed;
  opacity: 0.5;
}

.prepeers-checkbox__input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.prepeers-checkbox__box {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border: 1.5px solid var(--text-muted);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  transition:
    background-color var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out);
}

.prepeers-checkbox__label:hover
  .prepeers-checkbox__input:enabled:not(:checked)
  + .prepeers-checkbox__box {
  border-color: var(--accent-blue-lt);
}

.prepeers-checkbox__input:focus-visible + .prepeers-checkbox__box {
  outline: 2px solid var(--accent-blue-lt);
  outline-offset: 2px;
}

.prepeers-checkbox__input:checked + .prepeers-checkbox__box {
  border-color: var(--accent-blue);
  background-color: var(--accent-blue);
}

.prepeers-checkbox__box--error {
  border-color: var(--color-error);
}

.prepeers-checkbox__check {
  display: inline-flex;
  opacity: 0;
  transform: scale(0.6);
  transition:
    opacity var(--duration-fast) var(--ease-out),
    transform var(--duration-fast) var(--ease-out);
}

.prepeers-checkbox__check :deep(.prepeers-icon) {
  width: 12px;
  height: 12px;
}

.prepeers-checkbox__input:checked + .prepeers-checkbox__box .prepeers-checkbox__check {
  opacity: 1;
  transform: none;
}

.prepeers-checkbox__error {
  font-size: var(--text-xs);
  line-height: var(--leading-snug);
  color: var(--color-error);
}

@media (prefers-reduced-motion: reduce) {
  .prepeers-checkbox__box,
  .prepeers-checkbox__check {
    transition: none;
  }
}
</style>
